import classNames from 'classnames';
import { UnderlyingHistoModalConnected } from 'components/shared/UnderlyingHistos/UnderlyingHistoModal.connect';
import type { Underlying } from 'models/underlying';
import type { CreditUnderlying } from 'models/underlying/credit';
import type { EquityUnderlying } from 'models/underlying/equity';
import type { ForexUnderlying } from 'models/underlying/forex';
import { FC, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

interface ToolbarProps {
  hasClearAllButton: boolean;
  hasCopyAllButton: boolean;
  isLoading: boolean;
  underlyings?: Exclude<Underlying, CreditUnderlying | ForexUnderlying>[];

  onCopyAll: () => void;
  onClearAll: () => void;
}

export const Toolbar: FC<ToolbarProps> = ({
  hasCopyAllButton,
  hasClearAllButton,
  onClearAll,
  onCopyAll,
  isLoading,
  underlyings,
}) => {
  const { formatMessage } = useIntl();
  const copyAllLabel = useMemo(() => formatMessage({ id: 'underlying.copyAll' }), [formatMessage]);
  const clearAllLabel = useMemo(() => formatMessage({ id: 'underlying.clearAll' }), [formatMessage]);
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const equityUnderlyings = useMemo(
    () => underlyings?.filter((u): u is EquityUnderlying => u.asset === 'Equity') ?? [],
    [underlyings],
  );

  const hasEquityUnderlying = (equityUnderlyings?.length ?? 0) > 0;

  const shouldDisplayMultiGraph = hasEquityUnderlying;

  return (
    <div className="text-end mt-1" role="group">
      {shouldDisplayMultiGraph && (
        <div className="d-inline line-height-1 text-secondary align-items-center">
          <UnderlyingHistoModalConnected isDisabled={isLoading} isMulti={true} underlyings={equityUnderlyings} />
        </div>
      )}
      {hasClearAllButton && (
        <button
          className={classNames('btn btn-flat-secondary btn-icon-end', { 'border-end': hasCopyAllButton })}
          onClick={() => {
            logAnalytics.trackEvent(logAnalyticsCategory, 'Clear all underlyings');
            onClearAll();
          }}
          data-e2e="clear-all-underlyings"
          disabled={isLoading}
        >
          {clearAllLabel}
          <i className="icon ps-1">replay</i>
        </button>
      )}
      {hasCopyAllButton && (
        <button className="btn btn-flat-secondary btn-icon-end" onClick={onCopyAll}>
          {copyAllLabel}
          <i className="icon ps-1">file_copy</i>
        </button>
      )}
    </div>
  );
};
